import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";

const rejectedStatuses = ["REJECTED", "REFUSED"];

const toNumber = (value, fallback = 0) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
};

const isActiveObjective = (objectif) => !rejectedStatuses.includes(objectif.approvalStatus);

export const buildObjectifsAiStats = (objectifs, stepper) => {
    const activeObjectifs = objectifs.filter(isActiveObjective);
    const quantitatifs = activeObjectifs.filter((objectif) => objectif.typeObjectif === "QUANTITATIF").length;
    const qualitatifs = activeObjectifs.filter((objectif) => objectif.typeObjectif === "QUALITATIF").length;
    const poidsUtilise = activeObjectifs.reduce((sum, objectif) => sum + toNumber(objectif.poids), 0);

    const maxTotal = toNumber(stepper?.nombreMaxObjectifs);
    const maxQuantitatifs = toNumber(stepper?.nombreObjectifsQuantitatifs);
    const maxQualitatifs = toNumber(stepper?.nombreObjectifsQualitatifs);

    return {
        totalCrees: activeObjectifs.length,
        quantitatifsCrees: quantitatifs,
        qualitatifsCrees: qualitatifs,
        maxTotal,
        maxQuantitatifs,
        maxQualitatifs,
        quantitatifsRestants: Math.max(maxQuantitatifs - quantitatifs, 0),
        qualitatifsRestants: Math.max(maxQualitatifs - qualitatifs, 0),
        totalRestant: Math.max(maxTotal - activeObjectifs.length, 0),
        poidsDisponible: Math.max(100 - poidsUtilise, 0),
        poste: stepper?.jobName || stepper?.posteNom || "",
    };
};

export default function ObjectifsAIAssistanceModal({ open, onClose, stats, onGenerate }) {
    const [quantitatifs, setQuantitatifs] = useState(0);
    const [qualitatifs, setQualitatifs] = useState(0);
    const [poste, setPoste] = useState("");
    const [contexte, setContexte] = useState("");

    useEffect(() => {
        if (open) {
            setQuantitatifs(stats.quantitatifsRestants);
            setQualitatifs(stats.qualitatifsRestants);
            setPoste(stats.poste);
            setContexte("");
        }
    }, [open, stats]);

    const totalAGenerer = useMemo(
        () => toNumber(quantitatifs) + toNumber(qualitatifs),
        [quantitatifs, qualitatifs]
    );

    if (!open) return null;

    return (
        <div className="on-ai-modal-backdrop" onClick={onClose}>
            <div className="on-ai-modal" onClick={(event) => event.stopPropagation()}>
                <div className="on-ai-modal__header">
                    <h3>Assistance IA - Génération d'objectifs</h3>
                    <button className="on-ai-modal__close" type="button" onClick={onClose} aria-label="Fermer">
                        <X size={24} />
                    </button>
                </div>

                <div className="on-ai-modal__body">
                    <section className="on-ai-config">
                        <h4>Configuration des objectifs à générer</h4>
                        <div className="on-ai-grid on-ai-grid--2">
                            <label className="on-ai-field">
                                <span>Objectifs quantitatifs</span>
                                <input
                                    type="number"
                                    min="0"
                                    max={stats.quantitatifsRestants}
                                    value={quantitatifs}
                                    onChange={(event) => setQuantitatifs(event.target.value)}
                                />
                                <small>Maximum disponible : {stats.quantitatifsRestants}</small>
                            </label>

                            <label className="on-ai-field">
                                <span>Objectifs qualitatifs</span>
                                <input
                                    type="number"
                                    min="0"
                                    max={stats.qualitatifsRestants}
                                    value={qualitatifs}
                                    onChange={(event) => setQualitatifs(event.target.value)}
                                />
                                <small>Maximum disponible : {stats.qualitatifsRestants}</small>
                            </label>
                        </div>

                        <div className="on-ai-summary">
                            <strong>Total à générer : {totalAGenerer} objectifs</strong>
                            <strong>Poids disponible : {stats.poidsDisponible}%</strong>
                        </div>
                    </section>

                    <label className="on-ai-field">
                        <span>Poste *</span>
                        <input
                            value={poste}
                            onChange={(event) => setPoste(event.target.value)}
                            placeholder="Poste du collaborateur"
                        />
                    </label>

                    <label className="on-ai-field">
                        <span>Contexte/Missions *</span>
                        <textarea
                            rows={4}
                            value={contexte}
                            onChange={(event) => setContexte(event.target.value)}
                            placeholder="Décrivez les missions principales, priorités ou enjeux du poste..."
                        />
                    </label>
                </div>

                <div className="on-ai-modal__footer">
                    <button className="on-btn on-btn--outline" type="button" onClick={onClose}>
                        Annuler
                    </button>
                    <button
                        className="on-btn on-btn--accent"
                        type="button"
                        disabled={totalAGenerer <= 0 || !String(poste).trim() || !String(contexte).trim()}
                        onClick={() => onGenerate({ quantitatifs, qualitatifs, poste, contexte, poidsDisponible: stats.poidsDisponible })}
                    >
                        Générer les objectifs
                    </button>
                </div>
            </div>
        </div>
    );
}
